import React, {useEffect, useState, useRef, useCallback} from "react";
import PropTypes from "prop-types";
import Iframe from "react-iframe";
import { Modal } from "react-bootstrap";
import { checkOfflineState } from "./OfflineDetect";
import SubmitSearchButton from "./SubmitSearchButton";
import { getConfig } from "../lib/configMgr";
import _ from "lodash";
import {toast} from "react-toastify";
import Analytics from "../lib/Analytics";

const { ExternalRESTAPI } = getConfig();

export const isMolQueryPattern = (smiles = "") => {
  return /(\[H]|\*| \|\$[^$]*\$\|)/.test(smiles);
}


const MolecularEditorModal = ({onSearchFromEditor, onAddTerm, onReplaceTerm, onApplyFilter, showEditor, onCloseEditor,
                                encoding : input = "", forceSubstructureQuery = false, chatMode = false, mode = "search"}) => {
  const inputEncoding = showEditor ? (input || "").replace(/^(smiles|query|inchi|inchikey):/, "") : "";
  const inputIsQuery = input.startsWith("query:") || forceSubstructureQuery;
  //console.debug(`MolecularEditorModal: showEditor=${showEditor}, input='${input}', inputEncoding='${inputEncoding}'`);
  const [ketcher, setKetcher] = useState(null);
  const [initialEditorStructure, setInitialEditorStructure] = useState(null);
  const [editorStructure, setEditorStructure] = useState(null);
  const [isQuery, setIsQuery] = useState(inputIsQuery);
  const [currentSMILES, setCurrentSMILES] = useState(inputEncoding);
  const [isModified, setIsModified] = useState(false);
  const timerRef = useRef();

  const hasInput = inputEncoding.length > 0;

  useEffect(() => {
    if (inputEncoding && editorStructure?.isBlank() && initialEditorStructure === null) {
      setInitialEditorStructure(editorStructure.clone(editorStructure));
    }
  }, [inputEncoding, editorStructure, initialEditorStructure]);

  const handleEditorChange = useCallback(operations => {
    const s = ketcher?.editor?.struct();
    if (s?.isBlank()) {
      setEditorStructure(null);
    }
    else {
      setEditorStructure(s.clone());
      setIsQuery(isQueryMolecule(s) || forceSubstructureQuery);
    }
  }, [setEditorStructure, setIsQuery, ketcher, forceSubstructureQuery]);

  useEffect(() => {
    if (!editorStructure && !initialEditorStructure) {
      setCurrentSMILES("");
      setIsQuery(forceSubstructureQuery);
      setIsModified(false);
    }
    else if (_.isEqual(editorStructure, initialEditorStructure) && inputEncoding) {
      setCurrentSMILES(inputEncoding || "");
      setIsQuery(inputIsQuery || isMolQueryPattern(inputEncoding) || forceSubstructureQuery);
      setIsModified(false);
    }
    else {
      // Only look up SMILES value if the editor structure has actually changed
      fetchQueryTerm((term, isQuery, smiles) => {
        setCurrentSMILES(smiles);
        setIsQuery(isQuery || isMolQueryPattern(smiles) || forceSubstructureQuery);
        setIsModified(true);
      });
    }
  }, [editorStructure, initialEditorStructure, inputIsQuery]);

  useEffect(() => {
    if (showEditor) {
      Analytics.event(Analytics.EVENTS.KETCHER_OPEN, {});
      return () => {
        setKetcher(null);
        setEditorStructure(null);
        setInitialEditorStructure(null);
        setCurrentSMILES("");
        timerRef.current && clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }
  }, [showEditor])

  const fetchQueryTerm = (callback = (term, isQuery, smiles) => {}) => {
    // Determine if this is a query molecule (has atom lists, query features, etc.)
    const isQuery = isQueryMolecule() || forceSubstructureQuery;

    // Ketcher 3.x cancels its internal structService requests via AbortController,
    // which causes getSmiles()/getSmarts() to reject with "Failed to fetch".
    // Bypass this by: getting KET via getKet(), then calling indigo /convert directly.
    // PLEX-3419: atom lists can only be represented in SMARTS format
    const outputFormat = isQuery
      ? "chemical/x-daylight-smarts"
      : "chemical/x-chemaxon-cxsmiles";

    ketcher.getKet()
      .then(ket => fetch(`${ExternalRESTAPI.Indigo}/indigo/convert`, {
        method: "POST",
        headers: { "Content-Type": "chemical/x-indigo-ket", "Accept": outputFormat },
        body: ket,
      }))
      .then(response => {
        if (!response.ok) return response.text().then(t => { throw new Error(t); });
        return response.text();
      })
      .then(encoding => {
        const isQuerySMILES = isMolQueryPattern(encoding);
        const term = `${isQuery || isQuerySMILES ? "query" : "smiles"}:${encoding}`;
        callback(term, isQuery || isQuerySMILES, encoding);
        setIsQuery(isQuery || isQuerySMILES);
      })
      .catch(error => {
        const msg = `${error}`;
        toast.error(msg.replace("IndigoException: ", "").replace("Error: Convert error! ", ""));
        console.error("fetchQueryTerm error", error);
      });
  };

  const handleAddTerms = () => {
    fetchQueryTerm((term, isQuery, smiles) => { onCloseEditor(); onAddTerm(term, isQuery, smiles); });
  }

  const handleReplaceTerms = () => {
    fetchQueryTerm((term, isQuery, smiles) => { onCloseEditor(); onReplaceTerm(term, isQuery, smiles); });
  }

  const handleSubmitSearch = () => {
    onCloseEditor();
    fetchQueryTerm((term, isQuery, smiles) => {
      Analytics.event(Analytics.EVENTS.KETCHER_SEARCH, { smiles: (smiles || "").slice(0, 200) });
      onSearchFromEditor(term, isQuery, smiles);
    });
  };

  const handleApplyFilter = () => {
    fetchQueryTerm((term, isQuery, smiles) => {
      onCloseEditor();
      onApplyFilter(smiles);
    });
  };

  const onIFrameLoad = () => loadKetcher();

  const isQueryMolecule = (s = null) => {
    const structure = s || ketcher?.editor?.struct();
    if (!structure) {
      return false;
    }
    if (structure.isReaction || structure.rgroups.size || structure.rgroupAttachmentPoints.size) {
      return true;
    }
    // Partly cribbed from ketcher editor internals
    return _.some(structure.atoms.keys(), (atomID) => {
      const atom = structure.atoms.get(atomID);
      if (!atom) {
        return false;
      }
      // Look for anything that might make this a query
      if (atom.pseudo
          || atom.queryProperties?.customQuery
          || atom.attachmentPoints
          || atom.explicitValence !== -1
          || atom.rxnFragmentType !== -1
          || atom.atomList) {
        return true;
      }
      // Look for query atoms
      return (atom.isQuery() || Array.from(atom.sgs.values()).some((sGroupID) => {
        const sGroup = structure.sgroups.get(sGroupID);
        return sGroup && sGroup.type === "queryComponent";
      }));
    });
  };

  useEffect(() => {
    if (inputEncoding && ketcher) {
      // ketcher is only set after structService is confirmed present in loadKetcher()
      // Use async/await to ensure the molecule is set before capturing the structure
      (async () => {
        await ketcher.setMolecule(inputEncoding);
        // Capture the initial structure after the molecule has been set
        // Set both structures to the same value so they compare equal initially
        const struct = ketcher.editor.struct();
        if (struct && !struct.isBlank()) {
          setInitialEditorStructure(struct.clone());
          setEditorStructure(struct.clone());
        }
      })();
      setCurrentSMILES(inputEncoding);
    }
  }, [ketcher, inputEncoding])

  useEffect(() => {
    if (ketcher?.editor) {
      ketcher.editor.subscribe('change', handleEditorChange);
      return () => {
        ketcher.editor.unsubscribe('change', handleEditorChange);
      }
    }
  }, [ketcher, handleEditorChange]);

  // Close modal on ESC — iframe captures keyboard events so React Bootstrap's
  // built-in keyboard handling doesn't fire when the iframe has focus.
  useEffect(() => {
    if (!showEditor) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') onCloseEditor(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showEditor, onCloseEditor]);

  const loadKetcher = () => {
    const iframe = document.querySelector('iframe.mol-editor');
    const ko = iframe?.contentWindow?.ketcher || window?.frames['iframe-ketcher']?.window?.ketcher;
    // Wait for both editor and structService to be ready
    // structService is required for setMolecule to work without the
    // "Ketcher needs to be initialized before KetcherLogger is used" error
    if (ko?.editor && ko?.structService) {
      timerRef.current = null;
      setKetcher(ko);
    }
    else {
      timerRef.current = setTimeout(loadKetcher, 200);
    }
  };

  return (
    <Modal
      show={showEditor}
      onHide={onCloseEditor}
      size="lg"
      dialogClassName="ketcher-wrp-mdl"
    >
      <Modal.Header closeButton>
        <Modal.Title>{mode === "filter" ? "Substructure Filter" : "Molecular Editor"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Iframe
          id={"iframe-ketcher"}
          url={ExternalRESTAPI.Ketcher}
          className={"mol-editor"}
          width="850px"
          height="570px"
          display="initial"
          position="relative"
          onLoad={onIFrameLoad}
          allowFullScreen={true}
        />
      </Modal.Body>
      <Modal.Footer>
        <div className="smiles-list">
          {currentSMILES && currentSMILES !== ""
           ? (
             <div className="mol-editor-smiles">
               {currentSMILES} {isQuery ? (<span className={"sub"}>(substructure query)</span>) : ''}
             </div>
           )
           : ("No molecule defined")}
        </div>
        {mode === "filter" ? (
          <>
            <button
              className="btn"
              onClick={onCloseEditor}
              title="Cancel">
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleApplyFilter}
              disabled={!currentSMILES}
              title="Apply substructure filter to results">
              Apply Filter
            </button>
          </>
        ) : chatMode ? (
          <>
            <button
              className="btn"
              onClick={onCloseEditor}
              title="Cancel">
              Cancel
            </button>
            <button
              className="btn"
              onClick={handleAddTerms}
              disabled={!currentSMILES}
              title="Add structure to chat input">
              Add
            </button>
          </>
        ) : (
          <>
            <button
              className="btn"
              onClick={handleReplaceTerms}
              disabled={!currentSMILES || !isModified || !hasInput}
              title="Replace term with new structure">
              Replace
            </button>
            <button
              className="btn"
              onClick={handleAddTerms}
              disabled={!currentSMILES || !isModified}
              title="Add new structure to search terms">
              Add
            </button>
            <SubmitSearchButton
              className="btn mol-editor-submit-search"
              label="Search"
              flat={false}
              disabled={!currentSMILES}
              onSearch={checkOfflineState(handleSubmitSearch)}
            />
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

MolecularEditorModal.propTypes = {
  onSearchFromEditor: PropTypes.func
};

export default MolecularEditorModal;
